import { connect, type ConnectedProps } from 'react-redux';
import { InformationalScreen, LoadingModal, Stack, Text } from '@adc/parallax-component-library';
import {
  Footer,
  FormContainer,
  MainContainer,
  PhoneInput,
  TopHeaderBackArrow,
} from 'Components/utility';
import { StorageKeys } from 'Constants';
import { useFormik } from 'formik';
import { parsePhoneNumberFromString } from 'libphonenumber-js';
import { type RootState } from 'Reducers/index';
import { forgotPassword } from 'Services/verify';
import { notifyApp } from 'Utilities/appCommunicator';
import i18n from 'Utilities/i18n';
import mediator from 'Utilities/mediator';
import * as z from 'zod';
import { toFormikValidationSchema } from 'zod-formik-adapter';

const mapStateToProps = ({ env: { deviceId, osType, osVersion, app, appVersion } }: RootState) => ({
  deviceId,
  osType,
  osVersion,
  app,
  appVersion,
});

const connector = connect(mapStateToProps);

type Props = ConnectedProps<typeof connector>;

const ForgotPasswordPhone = ({ deviceId, osType, osVersion, app, appVersion }: Props) => {
  const validationSchema = z.object({
    phone: z
      .string(
        i18n.t('ForgotPasswordPhone.content.forgotPasswordForm.formField.phone.errors.required')
      )
      .refine((value) => parsePhoneNumberFromString(value)?.isPossible() ?? false, {
        error: i18n.t(
          'ForgotPasswordPhone.content.forgotPasswordForm.formField.phone.errors.phoneNumberValid'
        ),
      }),
  });

  const {
    handleSubmit,
    handleChange,
    handleBlur,
    isValid,
    dirty,
    errors,
    touched,
    values,
    isSubmitting,
  } = useFormik({
    initialValues: {
      phone: '',
    },
    validationSchema: toFormikValidationSchema(validationSchema),
    validateOnBlur: true,
    onSubmit: async (values) => {
      const { data } = await forgotPassword(
        values.phone,
        osType,
        osVersion,
        app,
        appVersion,
        deviceId
      );

      notifyApp('adc-webview:forgot-password', data);

      sessionStorage.setItem(StorageKeys.FORGOT_PASSWORD_PHONE, values.phone);

      mediator.publish('router:navigate', '/verify-phone');
    },
  });

  const handlePressBack = () => {
    mediator.publish('router:back');
    notifyApp('adc-webview:navigate-back-app');
  };

  return (
    <MainContainer>
      {isSubmitting && <LoadingModal aria-label="Loading" />}

      <InformationalScreen
        topNavigationProps={{
          animatedTitle: true,
          title: i18n.t('ForgotPasswordPhone.title'),
          buttonStart: {
            element: <TopHeaderBackArrow testID="Auth.action-go-back" />,
            label: i18n.t('ForgotPasswordPhone.title'),
            onPress: handlePressBack,
          },
          testID: 'Auth.actionPasswordSetupTitle',
        }}
      >
        <InformationalScreen.Page>
          <InformationalScreen.Content>
            <Text marginTop={-8}>{i18n.t('ForgotPasswordPhone.subtitle')}</Text>
            <Stack marginTop="$11">
              <FormContainer hasHiddenUserInput>
                <PhoneInput
                  testID="ForgotPassword.phone"
                  label={i18n.t(
                    'ForgotPasswordPhone.content.forgotPasswordForm.formField.phone.label'
                  )}
                  placeholder={i18n.t(
                    'ForgotPasswordPhone.content.forgotPasswordForm.formField.phone.placeholder'
                  )}
                  value={values.phone}
                  onChangeText={handleChange('phone')}
                  onBlur={handleBlur('phone')}
                  isInvalid={Boolean(errors.phone && touched.phone)}
                  errorMessage={errors.phone}
                />
              </FormContainer>
            </Stack>
          </InformationalScreen.Content>

          <InformationalScreen.Footer>
            <Footer
              buttonLabel={i18n.t(
                'ForgotPasswordPhone.content.forgotPasswordForm.links.send.label'
              )}
              onButtonPress={handleSubmit}
              buttonTestID="ForgotPasswordPhone.submit-btn"
              disabled={!isValid || !dirty}
            />
          </InformationalScreen.Footer>
        </InformationalScreen.Page>
      </InformationalScreen>
    </MainContainer>
  );
};

export default connector(ForgotPasswordPhone);
